import { Observable } from "rxjs";
import _ from "lodash";

import { st, CompanyRepository, ProductsRepository, CompanyFullDecorator } from "lib/index";
import { createFormInstance, createResourceInstance } from "modules/utils/index";
import { fetchCurrentCompany, FETCH_CURRENT_COMPANY_SUCCESS } from "modules/auth";

export const INITIALIZE_VIEW = "settings/PaymentsSettingsView/INITIALIZE_VIEW";
export const INITIALIZE_MODEL = "settings/PaymentsSettingsView/INITIALIZE_MODEL";
export const UPDATE_MODEL = "settings/PaymentsSettingsView/UPDATE_MODEL";
export const VALIDATE = "settings/PaymentsSettingsView/VALIDATE";
export const SUBMIT = "settings/PaymentsSettingsView/SUBMIT";
export const FETCH_SUBMIT_START = "settings/PaymentsSettingsView/FETCH_SUBMIT_START";
export const FETCH_SUBMIT_SUCCESS = "settings/PaymentsSettingsView/FETCH_SUBMIT_SUCCESS";
export const FETCH_SUBMIT_FAILURE = "settings/PaymentsSettingsView/FETCH_SUBMIT_FAILURE";
export const FETCH_PRODUCTS = "settings/PaymentsSettingsView/FETCH_PRODUCTS";
export const FETCH_PRODUCTS_START = "settings/PaymentsSettingsView/FETCH_PRODUCTS_START";
export const FETCH_PRODUCTS_SUCCESS = "settings/PaymentsSettingsView/FETCH_PRODUCTS_SUCCESS";
export const FETCH_PRODUCTS_FAILURE = "settings/PaymentsSettingsView/FETCH_PRODUCTS_FAILURE";
export const RELOAD_PAYMENT_PROFILES = "settings/PaymentsSettingsView/RELOAD_PAYMENT_PROFILES";

export const OPEN_INTEGRATION = "settings/IntegrateOnlinePaymentsModal/OPEN";
export const CLOSE_INTEGRATION = "settings/IntegrateOnlinePaymentsModal/CLOSE";
export const CLOSE_INTEGRATION_WITH_DATA = "settings/IntegrateOnlinePaymentsModal/CLOSE_WITH_DATA";

export const SET_FORM_STEP = "settings/IntegrateOnlinePaymentsModal/SET_FORM_STEP";
export const SET_INTEGRATION_TYPE = "settings/IntegrateOnlinePaymentsModal/SET_INTEGRATION_TYPE";
export const REMOVE_INTEGRATION = "settings/IntegrateOnlinePaymentsModal/REMOVE_INTEGRATION";

export const stateSelector = (state) => state.settings.PaymentsSettingsView;

const productsInstance = createResourceInstance({
  FETCH: FETCH_PRODUCTS,
  FETCH_START: FETCH_PRODUCTS_START,
  FETCH_SUCCESS: FETCH_PRODUCTS_SUCCESS,
  FETCH_FAILURE: FETCH_PRODUCTS_FAILURE,
  stateSelector,
  stateKey: "products",
  getModel: (state, store) => {
    const currentCompanyId = store.getState().auth.company.model.id;

    return ProductsRepository.search({
      limit: 100, // TODO: Review this if makes sense
      supplier_id: currentCompanyId,
    });
  },
});

const formInstance = createFormInstance({
  INITIALIZE_MODEL,
  UPDATE_MODEL,
  VALIDATE,
  SUBMIT,
  FETCH_SUBMIT_START,
  FETCH_SUBMIT_SUCCESS,
  FETCH_SUBMIT_FAILURE,
  stateSelector,
  validate: ({ form: { model } }, errors) => {
    errors.add(`model.default_product_price_currency`, st.validators.presence(model.default_product_price_currency));
  },
  submitModel: ({ form: { initialModel, model } }) => {
    const serialize = (company) => {
      const toSerialize = {
        id: company.id,
        bank_wire_account_description: company.bank_wire_account_description,
        default_product_price_currency: company.default_product_price_currency,
        increase_orders_amount_by_saas_commision: company.increase_orders_amount_by_saas_commision,
        company_payment_profile_relations: company.company_payment_profile_relations,
      };
      if ("dotpay_account_id" in company) {
        toSerialize.dotpay_account_id = company.dotpay_account_id;
      }
      if ("dotpay_pin" in company) {
        toSerialize.dotpay_pin = company.dotpay_pin;
      }
      if ("dotpay_api_login" in company) {
        toSerialize.dotpay_api_login = company.dotpay_api_login;
      }
      if ("dotpay_api_password" in company) {
        toSerialize.dotpay_api_password = company.dotpay_api_password;
      }
      if ("paypal_client_secret_key" in company) {
        toSerialize.paypal_client_secret_key = company.paypal_client_secret_key;
      }
      if ("paypal_client_api_key" in company) {
        toSerialize.paypal_client_api_key = company.paypal_client_api_key;
      }
      return toSerialize;
    };
    return CompanyRepository.update(serialize(model), serialize(initialModel));
  },
});

const initialState = {
  step: 0,
  serviceType: "paypal",
  opened_integration: false,
  form: formInstance.initialState,
  products: productsInstance.initialState,
};

const reducers = {
  ...formInstance.reducersMap,
  ...productsInstance.reducersMap,

  [INITIALIZE_VIEW]: () => initialState,
  [OPEN_INTEGRATION]: (state) => ({
    ...state,
    opened_integration: true,
  }),

  [CLOSE_INTEGRATION_WITH_DATA]: (state) => {
    return {
      ...state,
      opened_integration: false,
      serviceType: "paypal",
      step: 0,
    };
  },

  [CLOSE_INTEGRATION]: (state) => {
    let {
      paypal_client_api_key,
      paypal_client_secret_key,
      dotpay_account_id,
      dotpay_pin,
      dotpay_api_login,
      dotpay_api_password,
      ...model
    } = state.form.model;

    return {
      ...state,
      form: {
        ...state.form,
        model: new CompanyFullDecorator(model),
      },
      opened_integration: false,
      serviceType: "paypal",
      step: 0,
    };
  },

  [REMOVE_INTEGRATION]: (state) => {
    return {
      ...state,
      form: {
        ...state.form,
        model: new CompanyFullDecorator({
          ...state.form.model,
          paypal_client_api_key: null,
          paypal_client_secret_key: null,
          dotpay_account_id: null,
          dotpay_pin: null,
          dotpay_api_login: null,
          dotpay_api_password: null,
        }),
      },
      opened_integration: false,
      serviceType: "paypal",
      step: 0,
    };
  },

  [SET_FORM_STEP]: (state, { step }) => ({
    ...state,
    step,
  }),

  [SET_INTEGRATION_TYPE]: (state, { serviceType }) => {
    let { dotpay_account_id, dotpay_pin, dotpay_api_login, dotpay_api_password, ...payPalRest } = state.form.model;
    let { paypal_client_secret_key, paypal_client_api_key, ...dotPayRest } = state.form.model;
    let model = new CompanyFullDecorator(serviceType == "paypal" ? payPalRest : dotPayRest);
    return {
      ...state,
      form: {
        ...state.form,
        model,
      },
      serviceType,
    };
  },
};

const epics = [
  ...formInstance.epics,
  ...productsInstance.epics,

  (actions$, { getState }) =>
    actions$
      .ofType(INITIALIZE_VIEW)
      .merge(actions$.ofType(FETCH_CURRENT_COMPANY_SUCCESS))
      .map(() => {
        const company = getState().auth.company.model;
        return initializeModel(company);
      }),

  (actions$) =>
    actions$.ofType(INITIALIZE_VIEW).merge(actions$.ofType(FETCH_SUBMIT_SUCCESS)).mapTo(fetchCurrentCompany()),

  (actions$, { getState, dispatch }) =>
    actions$.ofType(RELOAD_PAYMENT_PROFILES).flatMap(() => {
      const { initialModel: initialCompany } = stateSelector(getState()).form;
      if (!initialCompany) {
        return Observable.empty();
      }
      return CompanyRepository.show()
        .flatMap((persistedCompany) => {
          const { model: dirtyCompany } = stateSelector(getState()).form;
          if (!dirtyCompany || dirtyCompany.id !== persistedCompany.id) {
            return Observable.empty();
          }

          const persistedCompanyRelationIds = persistedCompany.company_payment_profile_relations.map((rel) => rel.id);
          const dirtyCompanyRelationIds = dirtyCompany.company_payment_profile_relations.map((rel) => rel.id);
          const nextModel = new CompanyFullDecorator({
            ...dirtyCompany,
            company_payment_profile_relations: persistedCompany.company_payment_profile_relations
              .filter((rel) => !dirtyCompanyRelationIds.includes(rel.id))
              .concat(
                dirtyCompany.company_payment_profile_relations.filter((rel) =>
                  persistedCompanyRelationIds.includes(rel.id)
                )
              ),
          });
          return Observable.of(initializeModel(persistedCompany, nextModel));
        })
        .catch((error) => {
          st.api.handleError(error, { getState, dispatch });
          return Observable.of({ type: FETCH_PRODUCTS_FAILURE, error });
        });
    }),
];

export function initializeView() {
  return { type: INITIALIZE_VIEW };
}

export function reloadPaymentProfiles() {
  return { type: RELOAD_PAYMENT_PROFILES };
}

export function openIntegration() {
  return (dispatch, getState) => {
    dispatch({ type: OPEN_INTEGRATION });
  };
}

export function closeIntegration() {
  return (dispatch, getState) => {
    const { opened_integration } = stateSelector(getState());
    if (opened_integration) {
      dispatch({ type: CLOSE_INTEGRATION });
    }
  };
}

export function closeIntegrationWithoutDataErase() {
  return (dispatch, getState) => {
    const { opened_integration } = stateSelector(getState());
    if (opened_integration) {
      dispatch({ type: CLOSE_INTEGRATION_WITH_DATA });
    }
  };
}

export function setStep(step) {
  return (dispatch, getState) => dispatch({ type: SET_FORM_STEP, step });
}

export function selectIntegrationServiceType(serviceType) {
  return (dispatch, getState) => dispatch({ type: SET_INTEGRATION_TYPE, serviceType });
}

export function removeOnlineServiceIntegration() {
  return (dispatch, getState) => dispatch({ type: REMOVE_INTEGRATION });
}

export const { initializeModel, updateModel, validate, submit } = formInstance.actions;
export const { fetch: fetchProducts } = productsInstance.actions;

export default st.duck.createDuck(initialState, reducers, epics);



// WEBPACK FOOTER //
// ./src/modules/settings/PaymentsSettingsView/index.js